import { useEffect, useState } from 'react';
import { api, login, register, setTokens, type Me } from '../api.ts';
import { useApp } from '../store.tsx';
import { Field } from '../components.tsx';
import * as passkeys from '../passkeys.ts';

/** Sign in and sign up, with a passkey shortcut for a returning device. */
export function AuthScreen({ onAuthenticated }: { onAuthenticated: (me: Me) => void }): JSX.Element {
  const { t, locale, setLocale, toast } = useApp();
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [passkeyReady, setPasskeyReady] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mode === 'signup') {
        await register({ email, password, name: name || undefined, locale });
      } else {
        await login(email, password);
      }
      onAuthenticated(await api.get<Me>('/me'));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Only offered where the device can actually do it. A "Sign in with Face ID"
  // button on hardware with no biometrics is a promise the app cannot keep.
  useEffect(() => {
    let cancelled = false;
    passkeys.platformAuthenticatorAvailable().then((ok) => { if (!cancelled) setPasskeyReady(ok); });
    return () => { cancelled = true; };
  }, []);

  const signInWithPasskey = async () => {
    setError(null);
    setBusy(true);
    try {
      const result = await passkeys.login();
      setTokens(result.accessToken, result.refreshToken);
      onAuthenticated(await api.get<Me>('/me'));
    } catch (err) {
      // Dismissing Face ID is a decision, not a fault — saying "failed" here
      // reads as though the app is broken when nothing went wrong.
      if (!passkeys.wasCancelled(err)) setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="screen" style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', justifyContent: 'center', maxWidth: 440, marginInline: 'auto' }}>
      <div className="row row--between" style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 30 }} aria-hidden>◈</div>
        <button className="btn btn--ghost btn--sm" onClick={() => setLocale(locale === 'ar' ? 'en' : 'ar')}>
          {locale === 'ar' ? 'English' : 'العربية'}
        </button>
      </div>

      <h1 style={{ fontSize: 27, fontWeight: 690, letterSpacing: '-0.03em', lineHeight: 1.2 }}>
        {t('appName')}
      </h1>
      <p className="muted" style={{ marginTop: 6, marginBottom: 26, fontSize: 15 }}>{t('tagline')}</p>

      <form onSubmit={submit} className="stack">
        {mode === 'signup' && (
          <Field label={t('nameOptional')}>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)}
                   autoComplete="name" placeholder="—" />
          </Field>
        )}

        <Field label={t('email')}>
          <input className="input" type="email" required value={email}
                 onChange={(e) => setEmail(e.target.value)}
                 autoComplete="email" inputMode="email" placeholder="you@example.com" />
        </Field>

        <Field label={t('password')} hint={mode === 'signup' ? t('passwordHint') : undefined} error={error}>
          <input className="input" type="password" required minLength={mode === 'signup' ? 10 : 1}
                 value={password} onChange={(e) => setPassword(e.target.value)}
                 autoComplete={mode === 'signup' ? 'new-password' : 'current-password'} />
        </Field>

        <button className="btn btn--primary btn--block" type="submit" disabled={busy} style={{ marginTop: 4 }}>
          {busy ? (mode === 'signup' ? t('creatingAccount') : t('signingIn'))
                : (mode === 'signup' ? t('signUp') : t('signIn'))}
        </button>
      </form>

      {/* Sign-in only. There is no account to attach a passkey to during
          sign-up, so it is offered from Settings once one exists. */}
      {mode === 'signin' && passkeyReady && (
        <button className="btn btn--secondary btn--block" onClick={signInWithPasskey}
                disabled={busy} style={{ marginTop: 10 }}>
          <span aria-hidden>◉</span> {t('signInWithPasskey')}
        </button>
      )}

      {/* Only worth offering where it leads somewhere: the reset link is sent
          by email, and until now nothing was ever sent. */}
      {mode === 'signin' && (
        <button
          className="btn btn--ghost btn--sm center"
          style={{ marginTop: 10 }}
          onClick={async () => {
            if (!email.trim()) { setError(t('email')); return; }
            try {
              await api.post('/auth/forgot-password', { email: email.trim() });
              toast(locale === 'ar'
                ? 'إن كان البريد مسجلاً فقد أُرسل رابط.'
                : 'If that email is registered, a reset link is on its way.');
            } catch (err) {
              setError((err as Error).message);
            }
          }}
        >
          {t('forgotPassword')}
        </button>
      )}

      <div className="center small muted" style={{ marginTop: 18 }}>
        {mode === 'signin' ? t('noAccount') : t('haveAccount')}{' '}
        <button className="btn btn--ghost btn--sm" style={{ minHeight: 0, padding: 0, color: 'var(--accent)' }}
                onClick={() => { setMode(mode === 'signin' ? 'signup' : 'signin'); setError(null); }}>
          {mode === 'signin' ? t('signUp') : t('signIn')}
        </button>
      </div>
    </div>
  );
}
